/* eslint-disable no-underscore-dangle */
import { useEffect } from 'react';

import { rootDomain, cookieDomain, ExternalLinks, buildExternalURL } from '@mirinae/js-utils';

// pull in each apps local leaf paths (used in their local <Route>ing) to build absolute paths for every URL in every app, for sharing
import { localPaths as explorerLocalPaths } from '@mirinae/explorer/modules/defines/paths';
import { localPaths as membersLocalPaths } from '@mirinae/members/modules/defines/paths';
import { localPaths as lessonsLocalPaths } from '@mirinae/lessons/modules/defines/paths';
import { localPaths as wikinaeLocalPaths } from '@mirinae/wikinae/modules/defines/paths';
import { localPaths as lyricExplorerLocalPaths } from '@mirinae/lyric-explorer/modules/defines/paths';
import { localPaths as authLocalPaths } from '@mirinae/auth/modules/defines/paths';
import { localPaths as glossaryLocalPaths } from '@mirinae/glossary/modules/defines/paths';
import { localPaths as feedbackLocalPaths } from '@mirinae/feedback/modules/defines/paths';
import { localPaths as universityLocalPaths } from '@mirinae/university/modules/defines/paths';
import { localPaths as libraryLocalPaths } from '@mirinae/library/modules/defines/paths';
import { localPaths as kbeautyLocalPaths } from '@mirinae/guides/modules/defines/paths';

export { rootDomain, cookieDomain, ExternalLinks, buildExternalURL };

export var apiServer = '';  // always use app server host for now (will proxy to localhost:2005 in local dev server)

// the apps' root paths
export const appRoots = {
    explorer: 'explorer',
    lessons: 'lessons',
    members: 'members',
    wikinae: 'wikinae',
    lyricExplorer: 'lyric-explorer',
    auth: 'auth',
    university: 'university',
    // the public grammar library (mirinae-mono#101)
    library: 'library',
    // content sections (docs/content-marketing/content-sections-design.md), one root per category
    kbeauty: 'kbeauty',
    // the staff admin app, which also holds each school's own administration (docs/admin-app.md
    // section 5): /admin for mirinae pages, /admin/university/<alias> for one school's
    admin: 'admin',
    // glossary: 'glossary',  // these are not to be used for now, they have no GNB or footer, use app-local versions
    // feedback: 'feedback',  //    "   "
};

// construct full paths for all pages across all app
export const explorerPaths = Object.entries(explorerLocalPaths)
    .reduce((m, [pathKey, localPath]) => { m[pathKey] = `/${appRoots.explorer}${localPath ? '/' : ''}${localPath}`; return m; }, { });
export const lessonsPaths = Object.entries(lessonsLocalPaths)
    .reduce((m, [pathName, localPath]) => { m[pathName] = `/${appRoots.lessons}${localPath ? '/' : ''}${localPath}`; return m; }, { });
export const membersPaths = Object.entries(membersLocalPaths)
    .reduce((m, [pathName, localPath]) => { m[pathName] = `/${appRoots.members}${localPath ? '/' : ''}${localPath}`; return m; }, { });
export const wikinaePaths = Object.entries(wikinaeLocalPaths)
    .reduce((m, [pathName, localPath]) => { m[pathName] = `/${appRoots.wikinae}${localPath ? '/' : ''}${localPath}`; return m; }, { });
export const lyricExporerPaths = Object.entries(lyricExplorerLocalPaths)
    .reduce((m, [pathName, localPath]) => { m[pathName] = `/${appRoots.lyricExplorer}${localPath ? '/' : ''}${localPath}`; return m; }, { });
export const authPaths = Object.entries(authLocalPaths)
    .reduce((m, [pathName, localPath]) => { m[pathName] = `/${appRoots.auth}${localPath ? '/' : ''}${localPath}`; return m; }, { })
export const glossaryPaths = Object.entries(glossaryLocalPaths)
    .reduce((m, [pathName, localPath]) => { m[pathName] = `/${appRoots.glossary}${localPath ? '/' : ''}${localPath}`; return m; }, { })
export const feedbackPaths = Object.entries(feedbackLocalPaths)
    .reduce((m, [pathName, localPath]) => { m[pathName] = `/${appRoots.feedback}${localPath ? '/' : ''}${localPath}`; return m; }, { })
export const universityPaths = Object.entries(universityLocalPaths)
    .reduce((m, [pathKey, localPath]) => { m[pathKey] = `/${appRoots.university}${localPath ? '/' : ''}${localPath}`; return m; }, { });
export const libraryPaths = Object.entries(libraryLocalPaths)
    .reduce((m, [pathKey, localPath]) => { m[pathKey] = `/${appRoots.library}${localPath ? '/' : ''}${localPath}`; return m; }, { });
// A pattern's library page, from its stored slug (#103). The Korean is percent-encoded in the href, as Google
// asks; the address bar still shows it in Korean.
export const kbeautyPaths = Object.entries(kbeautyLocalPaths)
    .reduce((m, [pathKey, localPath]) => { m[pathKey] = `/${appRoots.kbeauty}${localPath ? '/' : ''}${localPath}`; return m; }, { });
export const libraryPatternPath = (slug) => `${libraryPaths.pattern}/${encodeURIComponent(slug)}`;

// A school's own administration, inside the staff admin app. It is a different bundle, so going
// there is a page load (navigateExternal), never a client-side navigate (mirinae-mono#26). Teachers
// and students stay in the university app at /university/<alias>.
export const orgAdminPath = (alias) => `/${appRoots.admin}/${appRoots.university}/${alias}`;

// A school's own login page -- where its people sign in and where signing out should leave them. The
// bare /university/login has no school in the address, so a teacher or student signing in there cannot
// be sent anywhere; the school's own page can (mirinae-mono#13). Falls back to the bare page only when
// no school is known.
export const schoolLoginPath = (alias) => (alias
    ? `/${appRoots.university}/${alias}/${universityLocalPaths.login}`
    : universityPaths.login);

// The university pages a guest may open: a school's own login and sign-up, with or without the school
// in the address (mirinae-mono#26), and a school's invitation page, which is where someone with no
// account yet arrives (#30) -- that one only with a school -- and a distributor's invitation page, which has none
// (#77 P77-2; `distributor-invitation` is a reserved school address, so it is no school's). The pattern lives here, with the other
// paths, so appBase does not have to import an app's own module.
const ALIAS_SEGMENT = '[a-z0-9][a-z0-9-]*';
export const universityEntryPath = new RegExp(
    `^/${appRoots.university}(?:(/${ALIAS_SEGMENT})?/(${universityLocalPaths.login}|${universityLocalPaths.signup})|/${ALIAS_SEGMENT}/${universityLocalPaths.invitation}|/${universityLocalPaths.distributorInvitation})$`
);

// Where a guest who may not stay on a university page is sent instead (#30). Not the explorer landing,
// as for the other apps: someone opening their school's address is a student or a teacher of it, and
// the school's sign-in is the page they need. The school's own address goes to its sign-in plainly,
// which sends a signed-in person on by their role; a page further in comes back after sign-in.
//
// The return address never carries the URL's fragment. An invitation's token lives there so that it
// reaches no server and no log; a redirect parameter would carry it to both.
export const universityGuestDestination = (pathname, search = '') => {
    const parts = pathname.split('/').filter(Boolean);   // ['university', alias?, ...]
    const alias = parts[1];
    if (!alias || !new RegExp(`^${ALIAS_SEGMENT}$`).test(alias)) return universityPaths.login;
    const login = schoolLoginPath(alias);
    if (parts.length === 2) return login;
    return `${login}?redirect=${encodeURIComponent(pathname + search)}`;
};

// A `?redirect=` value we will follow: a path on this site, and nothing else (#30). Checking how it starts
// is not enough: browsers drop tabs and line breaks from an address and read `\` as `/`, so `/<tab>/host`
// is another site. So those characters are refused outright, and what is left must still resolve to the
// same origin it was read against.
const SAME_SITE = 'https://same.invalid';
export const internalPath = (raw) => {
    if (typeof raw !== 'string' || !raw.startsWith('/') || raw.startsWith('//') || /[\u0000-\u001F\u007F\\]/.test(raw)) return null;
    try {
        return new URL(raw, SAME_SITE).origin === SAME_SITE ? raw : null;
    } catch (e) {
        return null;
    }
};

// external link redirector component
export const ExternalRedirector = ({ to, reload = false, versionParam }) => {
    const url = buildExternalURL(to, versionParam);
    useEffect(() => {
        if (reload)
            window.location.href = url;
        else
            window.location.replace(url);
    }, []);
    return null;
};

export const navigateExternal = (_url, options = {}) => {
    const url = buildExternalURL(_url, options.versionParam);
    if (options.replace) {
        window.location.replace(url);
    } else {
        window.location.href = url;
    }
};

// ad-hoc mapping of certain old /#/ URLs, as needed for new URL design
const pathMapping = (origApp, origPath, origQuery) => {
    let path = `${origApp}${origApp ? '/' : ''}${origPath}`;
    let query = origQuery.replace(/\/$/, '');
	// new root paths (independent of app)
    const newRoot = {
        'login': 'auth/login',
        'signup': 'auth/signup',
        'reset-password': 'auth/reset-password',
        'email-signup': 'auth/email-signup',
        'withdraw': 'auth/withdraw',
        'account-confirmed': 'auth/account-confirmed',
        // 'userFeedback': 'feedback',  don't share yet
    }[origPath];
    if (newRoot) {
        path = newRoot;
    } else if (origApp === 'explorer') {
        // fix incorrect mobile app URLs (not subdomain-based: https://app.mirinae.dev/?v=2.1.5/#/lessons)
        const newPath = {
            'lessons': 'lessons',
            'profile': 'members/profile',
            'library': 'explorer/library',
            'glossary': 'explorer/glossary',
        }[origPath];
        if (newPath) {
            path = newPath;
        }
    } else {
        // new root paths by app
        const newRootByApp = {
            // 'explorer/glossary': 'glossary', don't share yet, need to refactor header & footer to be app-independent
        }[path];
        if (newRootByApp) {
            path = newRootByApp;
        }
    }
    if (path.endsWith('.')) path += '/'; // wikinae. hack
	return [path, query];
};


// handle old /#/... urls via a server redirect
export const redirectHashURLs = (isApp, defaultHome) => {
      
    if (location.hash.startsWith('#/') || (location.hostname.split('.').length > 2 && !location.hostname.match(/^(alpha|seoul|local)\./))) {
        // parse hostname, which may include any of our original subddomains, ignoring any alpha
        // or app, to gen the new top-level, app path elemnents
        
        let rootDomain = location.hostname.includes('.') ? location.hostname.match(/((.+\.)|(^))([a-z\-]+\.[a-z][a-z][a-z]*)$/)[4] : location.hostname;
        const alpha = location.hostname.startsWith('alpha.') ? 'alpha.' : location.hostname.startsWith('seoul.') ? 'seoul.' : '';
        const local = location.hostname.startsWith('local.') ? 'local.' : '';
        const baseHostName = location.hostname.replace(/(alpha\.)|(seoul\.)|(app\.)|(alpha\.app\.)|(local\.)/, '');
        let app = baseHostName.replace(new RegExp(`[.]?${rootDomain}$`), '') || 'explorer'; // for now, app path prefix from #-url subdomain
        app = app.replace(/app\.?/, '');
        if (isApp && app === 'wikinae') app = 'wikinae.';  // hack so old mobile apps will display wikinae pages correctly (they search for 'wikinae.' to condition screen for wikinae!)
        const port = location.port ? `:${location.port}` : '';
        // extract hash-component as rest of path
        const [, origPath, origQuery] = location.hash ? location.hash.match(/^#\/(.*?)($|(\?.*$))/) : ['', '', location.search]; // this is the original pathing, we may transform it at some point further
        const [path, query] = pathMapping(app, origPath, origQuery || location.search); // allow for any re-mapping for new pathing
        const lang = ''; // '/en'; // for now, get it from Localization when shared
        const url = `${location.protocol}//${alpha}${local}${rootDomain}${port}${lang}/${path}${query || ''}`;

        console.log('redirect', location.href, 'to', url);
        // assign to location.href to force a server visit for SEO purposes
        window.location.href = url;
        return null;
    }

    return defaultHome;
}

// this is depracacted, one of the original path namespaces from the separated apps (Explorer in this case)
export const pagePaths = {
    root: '/',
    home: '/',
    // gate: '/gate',
    reference: '/reference',
    library: '/library',
    glossary: '/glossary',
    help: '/help',
    login: '/login',
    signup: '/signup',
    emailSignup: '/email-signup',
    resetPassword: '/reset-password',
    history: '/explorer/history',
    // bookmark: '/bookmark',
    // setting: '/setting',
    withdraw: '/withdraw',
    // welcome: '/welcome',
    landing: '/landing',
    tutorial: '/tutorial',
    // show404: '/404.html',
    // oldBrowser: '/oldbrowser.html',
    userFeedback: '/userFeedback',
    feedback: '/user-feedback',
    ocr: '/ocr',
    accountConfirmed: '/account-confirmed/:signupBy',
    wikinae: '/wikinae',

    contents: '/lyrics',
    artists: '/artists',
    artist: '/artist',
    music: '/music',
    song: '/song',

    lessons: '/lessons',
    features: '/features',
    myLesson: '/lessons',
    myLessons: '/lessons',
    lessonPlayer: '/lessons/player',
    lessonsFeatures: '/lessons/features',
    lessonsSurvey: '/lessons/survey',

    plan: '/plan',
    myAccount: '/profile',
    pro: '/members/',
    payment: '/members/payment',
    profile: '/members/profile',
    wikinaeHome: '/wikinae/',

    // wikinae
    wikinaeEntry: '/wikinae/entry',
};


export const apiPaths = {
    check: '/api/auth/check',
    loginAsReturn: '/api/auth/loginas/return',   // back from admin login-as
    localLogin: '/api/auth/login/local',
    guestSignup: '/api/auth/signup/guest',
    localSignup: '/api/auth/signup/local',
    googleLogin: '/api/auth/login/google',
    facebookLogin: '/api/auth/login/facebook',
    twitterLogin: '/api/auth/check/twitter',
    logout: '/api/auth/logout',
    sendMail: '/api/auth/send-mail',
    verifyOpt: '/api/auth/verify-opt',
    resetLinkCheck: '/api/auth/reset-link/check',
    reset: '/api/auth/reset-password',
    deleteAccount: '/api/auth/delete',
    updatePrefs: '/api/user/prefs/update',
    appleLogin: '/api/auth/mobile/apple', // piggy-back on mobile apple login, same response & processing for web

    // user notes
    saveNote: '/api/notes/save-note',
    deleteNote: '/api/notes/delete-note',
    listNotes: '/api/notes/list',
    // user feedback
    sendFeedback: '/api/feedback/send-feedback',
    // exploration history   **** note that for now these are directed to the NLP back-end server, should be handled by front-end-server in time ***
    bookmark: '/api/nlp/bookmark',
    listHistory: '/api/nlp/hist/list',
    patternHistory: '/api/nlp/hist/for-pattern',
    getBookmark: '/api/nlp/bookmark/get',
    deleteBookmark: '/api/nlp/bookmark/delete',
    deleteAllBookmarks: '/api/nlp/bookmark/delete-all',
    // reference library
    versionCheck: '/api/nlp/reference/check',
    listReferences: '/api/nlp/reference/list',
    getSource: '/api/nlp/source/get',
    // the grammar library's reads, open to guests (front-end api/nlp/library.js, mirinae-mono#104)
    libraryPattern: '/api/nlp/library/pattern',
    libraryList: '/api/nlp/library/list',
    libraryExtRefs: '/api/nlp/library/extrefs',
    libraryTree: '/api/library/tree',

    // reward access keys
    accessFeatures: '/api/user/key/accessFeatures',
    grantKey: '/api/user/key/grantKey',
    listActiveAdUnits: '/api/user/key/listActiveAdUnits',

    // glossary library
    listGlossary: '/api/nlp/glossary/list',

    // try these sentence
    tryThese: '/api/sentence/list-try?client=true',
    tryWikinae: '/api/sentence/todays-wikinae',

    // withdrawal
    withdrawalSurvey: '/api/withdrawal/list-survey?client=true',

    getOcr: '/api/ocr/get',

    // threads board
    addPost: '/api/social/post/add', // POST - adds a new user post
    getPost: '/api/social/post/get', // GET - /api/social/post/get/:postID - retrieves retrieves IDed post 
    updatePost: '/api/social/post/update', // POST - /api/social/post/update/:postID  - update post 
    deletePost: '/api/social/post/delete', // POST - /api/social/post/delete/:postID - delete a post (marks as deleted in status) 
    votePost: '/api/social/post/vote', // POST - /api/social/post/vote/:postID - vote a post up or down 
    report: '/api/social/post/report', // reports possible bad post 
    reportUpdate: '/api/social/post/report/update', // update a report
    postList: '/api/social/post/list', // GET - get list of postings under filter, sort & pagination
    // '/api/social/post/reports',  // get list of post reports under filter, sort & pagination  /api/social/tag/set       // set tags on a given object
    // '/api/social/tag/list',      // list objects tagged with tag, w/filter, sort & pagination
    like: '/api/social/like', // POST - short-cut to add #like tagging to an object
    unlike: '/api/social/unlike', // POST - short-cut to remove #like tagging to an object  
    reportReasonList: '/api/social/report/reasons/list?client=true',

    // profile
    updateName: '/api/user/name/update',
    updateEmail: '/api/user/email/update',
    updateProfileImage: '/api/user/profile/image/update',

    // paypal payment
    addSubscription: '/api/payment/paypal/addsubscription',
    cancelSubscription: '/api/payment/paypal/cancelsubscription',

    // promotion
    promotionList: '/api/promotion/read',

    sendVerification: '/api/auth/signup/send-verification',
    // check: '/api/auth/check',

    checkLocalEmail: '/api/auth/check/local-email',

    // help center
    helpList: '/api/help/list',

    // wikinae entries
    entryList: '/api/wikinae/entry/list',
    entryView: '/api/wikinae/entry/view',
    entryBookmark: '/api/wikinae/entry/bookmark', // add or delete a bookmark
    bookmarks: '/api/wikinae/bookmarks', // lists user's bookmark
    search: '/api/wikinae/search',
    searchterms: '/api/wikinae/searchterms',
    usersearchterms: '/api/wikinae/usersearchterms',
    clearusersearchterms: '/api/wikinae/usersearchterms/clear',

    // lesson player API
    lessonList: '/api/lessons/list', // POST - get list of availabel lessons (no search or pagination support yet)
    lessonListLight: '/api/lessons/list-light', // POST - lightweight version without expensive social lookups
    getMyLessons: '/api/lessons/getMyLessons', // POST - get a list of lessons I have taken. in activity-date-reverse order, including progress %
    getLesson: '/api/lessons/getLesson', // GET /api/lessons/getLesson/:lessonID - get identified lesson object
    getLessonProgress: '/api/lessons/progress', // GET /api/lesson/progress/:lessonID - request latest progress on objectIDed lesson
    postEvent: '/api/lessons/postEvent', // POST /api/lesson/event/:lessonID - post lesson user event on objectIDed lesson
    resetProgress: '/api/lessons/resetProgress', // POST /api/lesson/resetProgress/:lessonID - reset progress on objectIDed lesson
    elementImage :'/api/lessons/element/image', // GET /api/lessons/element/image/:figmaKey' - retrieves element with given figmaKey
    gatherFigmaAssets: '/api/lessons/gatherfigmaassets',
    getLessonsList: '/apt/lessons/list',
    topLessons: '/api/lessons/toplessons', // get top-ranked lessons
    viewCounts: '/api/lessons/viewcounts',
    graphicGet: '/api/lessons/graphic/get',

    // lesson editor APIs
    updateNodeMap: '/api/lessons/updatenodemap',
    updateNodeMapByGuid: '/api/lessons/updatenodemapbyguid',
    lessonsByFigmaID: '/api/lessons/lessonsbyfigmaid',
    lessonsForEditor: '/api/lessons/lessonsforeditor',
    snapshot: '/api/lessons/snapshot',
    backup: '/api/lessons/backup',
    listBackups: '/api/lessons/listbackups',
    getBackup: '/api/lessons/getBackup',
    login: '/api/auth/login/local',
    getFigmaFileJSON: '/api/lessons/getfigmafilejson',
    saveClipboard: '/api/lessons/saveclipboard',
    getClipboard: '/api/lessons/getclipboard',
    export: '/api/lessons/export',
    import: '/api/lessons/import',
    editLock: '/api/lessons/editlock',
    lesson: '/api/lessons/lesson',
    globalSearch: '/api/lessons/globalsearch',
    lessonSearch: '/api/lessons/search',
    getLessonSpec: '/api/lessons/spec',
    postSpec: '/api/lessons/spec',
    updateLessonSet: '/api/lessons/updateset',
    updateLessonFlag: '/api/lessons/updateflag',
    assignEditor: '/api/lessons/assigneditor',
    graphicUpload: '/api/lessons/graphic/upload',

    // NLP API used by lessons editor
    setLesson: '/api/nlp/pattern/setlesson',
    getPageOfPatterns: '/api/nlp/pattern/getpageof',
    getPattern: '/api/nlp/pattern/get/',

    // old lessons API
    createLesson: '/api/lessons/create',
    createPage: '/api/lessons/page/create',
    updateDB: '/api/lessons/updatedb',

};
